import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFrameStable } from "@rapid/contexts/FrameContext";
import { generateVideoThumbnails, ThumbnailMode } from "@rapid/utils/videoEditing";
import { MediaItem } from "@rapid/types/media";
interface VideoTimelinePreviewsOptions {
    mediaItem?: MediaItem;
    totalFrames: number;
    fileDuration: number;
    keyframeOffset: number;
    onFramePreviewsReady?: () => void;
    isVideoReadyForSeeking: boolean;
    setIsVideoReadyForSeeking: (ready: boolean) => void;
    videoRef?: React.RefObject<HTMLVideoElement | null>;
    thumbnailGeneratorOpts: {
        size: number;
        quality: number;
        mode: ThumbnailMode;
        numPreviews: number;
        defaultFramePreviews: string[];
    };
}

export function useVideoTimelinePreviews({
    mediaItem,
    totalFrames,
    fileDuration,
    keyframeOffset,
    onFramePreviewsReady,
    isVideoReadyForSeeking,
    setIsVideoReadyForSeeking,
    videoRef,
    thumbnailGeneratorOpts
}: VideoTimelinePreviewsOptions) {
    const { orderedTimelineFramePreviewUrls, setOrderedTimelineFramePreviewUrls } =
        useFrameStable();

    const [isGeneratingPreviews, setIsGeneratingPreviews] = useState(false);

    const processedFiles = useRef<Map<string, boolean>>(new Map());
    // Stable callback references - these won't change unless their dependencies change
    const stableOnFramePreviewsReady = useCallback(() => {
        onFramePreviewsReady?.();
    }, [onFramePreviewsReady]);

    const stableSetIsVideoReadyForSeeking = useCallback(
        (ready: boolean) => {
            setIsVideoReadyForSeeking(ready);
        },
        [setIsVideoReadyForSeeking]
    );

    const defaultFramePreviews = useMemo(() => {
        return thumbnailGeneratorOpts.defaultFramePreviews || [];
    }, [thumbnailGeneratorOpts.defaultFramePreviews]);

    const cachedPreviews = useMemo(() => {
        if (!mediaItem?.id || !totalFrames) return defaultFramePreviews;

        const previews = orderedTimelineFramePreviewUrls[mediaItem.id]?.[totalFrames];
        if (!previews) return defaultFramePreviews;

        return previews;
    }, [orderedTimelineFramePreviewUrls, mediaItem?.id, totalFrames, defaultFramePreviews]);

    // Main effect for preview generation - only trigger on essential changes
    useEffect(() => {
        if (!mediaItem?.id || !mediaItem?.file || !totalFrames) {
            return;
        }

        const { numPreviews, defaultFramePreviews, ...generatorOpts } = thumbnailGeneratorOpts;

        const performPreviewGeneration = async () => {
            // Capture the current values to avoid closure issues when mediaItem changes
            const targetVideoId = mediaItem.id;
            const targetTotalFrames = totalFrames;
            const targetFile = mediaItem.file;
            const compositeKey = `${targetVideoId}-${targetTotalFrames}`;

            // Skip if already triggered for this specific video/totalFrames combination
            if (processedFiles.current.has(compositeKey)) {
                return;
            }

            setIsGeneratingPreviews(true);

            try {
                // Create array of thumbnail requests for all preview positions
                const thumbnailRequests = Array.from({ length: numPreviews }, (_, i) => {
                    const relativeTimePosition = (i / (numPreviews - 1)) * fileDuration;
                    const absoluteTimePosition = relativeTimePosition + keyframeOffset;

                    return { currentTime: absoluteTimePosition };
                });

                processedFiles.current.set(compositeKey, true);

                // Generate all thumbnails in serialized, ordered fashion
                const results = await generateVideoThumbnails(
                    targetFile,
                    thumbnailRequests,
                    generatorOpts
                );

                // single bulk update
                if (targetVideoId && results.length > 0) {
                    // Extract thumbnails from results, maintaining order
                    const previews = results
                        .map((result) => result.thumbnail)
                        .filter((thumbnail): thumbnail is string => thumbnail !== null);

                    setOrderedTimelineFramePreviewUrls((prev) => {
                        const newPreviews = {
                            ...prev,
                            [targetVideoId]: {
                                ...(prev[targetVideoId] || {}),
                                [targetTotalFrames]: previews
                            }
                        };
                        return newPreviews;
                    });
                }

                if (videoRef?.current && !isVideoReadyForSeeking) {
                    stableSetIsVideoReadyForSeeking(true);
                }
            } catch (error) {
                // Handle different types of errors appropriately
                console.error(
                    `🎬 [useVideoTimelinePreviews] 🎬 performPreviewGeneration Error`,
                    error
                );
            } finally {
                setIsGeneratingPreviews(false);
                stableOnFramePreviewsReady();
            }
        };

        performPreviewGeneration();

        return () => {
            setIsGeneratingPreviews(false);
        };
    }, [
        // Core dependencies that should trigger new preview generation
        mediaItem?.id,
        mediaItem?.name,
        mediaItem?.file,
        totalFrames,
        fileDuration,
        keyframeOffset,
        // State dependencies needed for logic
        isVideoReadyForSeeking,
        videoRef,
        // Stable callbacks and functions
        stableOnFramePreviewsReady,
        stableSetIsVideoReadyForSeeking,
        thumbnailGeneratorOpts,
        setOrderedTimelineFramePreviewUrls
    ]);

    return { orderedFramePreviews: cachedPreviews, isGeneratingPreviews };
}
